import type {
  GoodsReceiptSource,
  PurchaseOrderEditor,
  PurchaseOrderSource,
  PurchasingSource,
  ReportSource,
  RfqSource,
  SupplierSource,
} from '@felix/ui';
import { officeApi } from './api.js';
import { quotationDocumentUrl, uploadQuotationDocument } from './quotation-documents.js';

/**
 * Ofiste satın alma (ADR 0026 §1, §3): talep gemide yazılır, ofiste görülür; ofisteki onay
 * adımı burada parolayla karara bağlanır ve yalnız o gemiye iner. Talep yazma (`editor`) yok.
 */
/** Ofiste RFQ ve teklif karşılaştırması (ADR 0026 §1; M12b Task 14): gemiye inmez. */
export const officeRfqSource: RfqSource = {
  list: () => officeApi.rfqs(),
  get: (id) => officeApi.rfq(id),
  create: (input) => officeApi.createRfq(input),
  invite: (id, supplierId) => officeApi.inviteRfqSupplier(id, supplierId),
  send: (id, rowVersion) => officeApi.sendRfq(id, rowVersion),
  saveQuotation: (id, supplierId, input) => officeApi.saveQuotation(id, supplierId, input),
  select: (id, input) => officeApi.selectQuotation(id, input),
  selectLines: (id, input) => officeApi.selectRfqLines(id, input),
  cancel: (id, input) => officeApi.cancelRfq(id, input),
  publish: (id, input) => officeApi.publishRfq(id, input),
  // Ödül (ADR 0111 §D)
  awards: {
    get: (rfqId) => officeApi.rfqAwards(rfqId),
    create: (rfqId, input) => officeApi.createAward(rfqId, input),
    propose: (awardId, input) => officeApi.proposeAward(awardId, input),
    acceptCounter: (awardId, input) => officeApi.acceptAwardCounter(awardId, input),
    finalize: (awardId, input) => officeApi.finalizeAward(awardId, input),
    cancel: (awardId, input) => officeApi.cancelAward(awardId, input),
    comment: (awardId, input) => officeApi.commentAward(awardId, input),
  },
  suppliers: () => officeApi.suppliers(),
  acceptSupplier: (supplierId) => officeApi.acceptPortalSupplier(supplierId),
  partners: (query) => officeApi.partners(query),
  // Kayıtsız tedarikçi: şirket içi kart ve teklif belgesi (ADR 0168; D1)
  createSupplier: (input) => officeApi.createSupplier(input),
  attachQuotationDocument: (id, quotationId, file) =>
    uploadQuotationDocument(id, quotationId, file),
  quotationDocumentUrl,
};

export const officePurchasingSource: PurchasingSource = {
  scope: 'web',
  vessels: () => officeApi.vessels(),
  requisitions: () => officeApi.requisitions(),
  requisition: (id) => officeApi.requisition(id),
  decide: (id, input) => officeApi.decideRequisition(id, input),
  catalog: (query) => officeApi.materials(query),
  rfqs: officeRfqSource,
  // Ofis onaylı talebi düzenler (ADR 0111 §A)
  revise: (id, input) => officeApi.reviseRequisition(id, input),
};

/** Ofiste tedarikçiler (ADR 0026 §1): ofisin ticari verisi, gemiye inmez. */
export const officeSupplierSource: SupplierSource = {
  list: () => officeApi.suppliers(),
  create: (input) => officeApi.createSupplier(input),
  update: (id, rowVersion, input) => officeApi.updateSupplier(id, rowVersion, input),
  portal: {
    invite: (id, input) => officeApi.inviteSupplierToPortal(id, input),
    revoke: (id, reason) => officeApi.revokeSupplierPortal(id, reason),
    accept: (id) => officeApi.acceptPortalSupplier(id),
  },
  // Kartta şirket profili, banka hesapları ve dört göz incelemesi (ADR 0114)
  profile: {
    get: (id) => officeApi.supplierProfile(id),
    reveal: (id, versionId) => officeApi.revealSupplierBank(id, versionId),
    review: (id, versionId, input) => officeApi.reviewSupplierBank(id, versionId, input),
    // Partner yorumları (ADR 0120 §4)
    reviews: (id) => officeApi.supplierReviews(id),
    writeReview: (id, input) => officeApi.writeSupplierReview(id, input),
  },
};

/** Şirket markasının adı (PDF başlığı; ADR 0019 §4). */
async function brandName(): Promise<string | null> {
  try {
    return (await officeApi.branding()).branding?.name ?? null;
  } catch {
    return null;
  }
}

/** Ofiste sipariş yazma (ADR 0040): açma, onay (parolayla), gönderim, teyit, iptal, kapanış. */
export const officePurchaseOrderEditor: PurchaseOrderEditor = {
  candidates: () => officeApi.purchaseOrderCandidates(),
  create: (rfqId) => officeApi.createPurchaseOrder(rfqId),
  update: (id, rowVersion, input) => officeApi.updatePurchaseOrder(id, rowVersion, input),
  submit: (id, input) => officeApi.purchaseOrderAction(id, 'submit', input),
  decide: (id, input) => officeApi.purchaseOrderAction(id, 'decision', input),
  send: (id) => officeApi.purchaseOrderAction(id, 'send', {}),
  markSent: (id, rowVersion) => officeApi.purchaseOrderAction(id, 'mark-sent', { rowVersion }),
  confirm: (id, input) => officeApi.purchaseOrderAction(id, 'confirm', input),
  cancel: (id, input) => officeApi.purchaseOrderAction(id, 'cancel', input),
  close: (id, input) => officeApi.purchaseOrderAction(id, 'close', input),
};

/** Ofiste siparişler (ADR 0026 §1): ofiste yazılır; PDF tarayıcıda. */
export const officePurchaseOrderSource: PurchaseOrderSource = {
  scope: 'web',
  vessels: () => officeApi.vessels(),
  list: () => officeApi.purchaseOrders(),
  get: (id) => officeApi.purchaseOrder(id),
  brandName,
  editor: officePurchaseOrderEditor,
};

/** Ofiste mal kabulü salt okunur (ADR 0026 §1): GRN gemide yazılır, senkronla gelir. */
export const officeGoodsReceiptSource: GoodsReceiptSource = {
  scope: 'web',
  vessels: () => officeApi.vessels(),
  list: () => officeApi.goodsReceipts(),
  get: (id) => officeApi.goodsReceipt(id),
};

/** Ofiste raporlar (ADR 0040 #189): görülebilen gemiler; Excel ve PDF tarayıcıda. */
export const officeReportSource: ReportSource = {
  scope: 'web',
  vessels: () => officeApi.vessels(),
  get: (report, query) => officeApi.report(report, query),
  brandName,
};
